import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Switch,
  Checkbox,
  HStack,
  Button,
  ModalFooter,
  useToast,
} from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import context from '../context';
import Datepicker from '../../Form/Datepicker';
import {
  CREATE_PAGE_GROUP,
  REMOVE_PAGE_GROUP,
  UPDATE_PAGE_GROUP,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import Input from '../../Form/Input';
import Textarea from '../../Form/Textarea';
import sanitizeString from '../../../utils/sanitizeString';

interface Props {
  onCreated?: (data: any) => void;
}
interface FormData {
  reference_id: string;
  description?: string;
  countdown: boolean;
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
}

const formData = yup.object().shape({
  reference_id: yup.string().required('ID de uma campanha é obrigatório'),
  schedule: yup.boolean(),
  description: yup.string().nullable(),
  countdown: yup.boolean(),
  schedule_start: yup
    .mixed()
    .when('schedule', {
      is: true,
      then: yup.date().required('Data de início é obrigatória'),
    })
    .optional(),
  schedule_end: yup
    .mixed()
    .when(['schedule', 'schedule_start'], (schedule, schedule_start) => {
      if (!schedule) {
        return yup.date();
      }
      if (schedule_start) {
        return yup
          .date()
          .min(schedule_start, 'Data final deve ser superior a data inicial')
          .required('Data Final é obrigatória');
      }
      return yup.date().required('Data Final é obrigatória');
    }),
});

const Form: React.FC<Props> = ({ onCreated }) => {
  const toast = useToast();

  const { pageGroup, newPageGroupDTO, onPageGroupCreated, onClose, display } =
    React.useContext(context);

  const { formState, handleSubmit, register, control, setValue } =
    useForm<FormData>({
      resolver: yupResolver(formData),
    });

  const [createPageGroup, { loading: createLoading, error: createError }] =
    useMutation(CREATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [updatePageGroup, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [removePageGroup, { loading: deleteLoading, error: deleteError }] =
    useMutation(REMOVE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [schedule, setSchedule] = useState(!!pageGroup?.schedule_end);

  const { errors } = formState;

  useEffect(() => {
    if (createError || updateError || deleteError) {
      toast({
        title: 'Opss!!',
        description:
          createError?.message ||
          updateError?.message ||
          deleteError?.message ||
          'Tivemos um erro, tente novamente mais tarde',
        status: 'error',
        position: 'top-right',
      });
    }
  }, [createError, updateError, toast, deleteError]);

  useEffect(() => {
    if (!pageGroup) return;
    if (pageGroup?.schedule_end) {
      setValue('schedule', true);
      setValue('schedule_start', new Date(pageGroup.schedule_start));
      setValue('schedule_end', new Date(pageGroup.schedule_end));
      setValue('countdown', pageGroup.countdown);
    }
    setValue('name', pageGroup.name);
    setValue('description', pageGroup?.description);
    setValue('reference_id', pageGroup?.reference_id);
  }, [pageGroup, setValue]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const sanitizedObj = sanitizeString(data);

      const { schedule: scheduleData, ...rest } = sanitizedObj;

      if (pageGroup) {
        await updatePageGroup({
          variables: {
            updatePageGroupInput: {
              ...rest,

              reference_id: data?.reference_id,
              schedule_end: scheduleData ? rest.schedule_end : null,
              countdown: scheduleData ? rest.countdown : false,
              id: pageGroup?.id,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        toast({
          title: 'Sucesso!',
          description: 'Grupo editado com sucesso!!',
          status: 'success',
          position: 'top-right',
        });
      } else {
        const apiData = await createPageGroup({
          variables: {
            createPageGroupInput: {
              ...rest,
              ...newPageGroupDTO,
              element_display_id: display?.id,

              reference_id: data?.reference_id,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        if (onCreated) onCreated(apiData);
        if (onPageGroupCreated) onPageGroupCreated(apiData);
        toast({
          title: 'Sucesso!',
          description: 'Grupo criado com sucesso!!',
          status: 'success',
          position: 'top-right',
        });
      }
    } catch {
      // nada
    }
  };

  const handleDelete = async () => {
    try {
      const confirmed = window.confirm(
        'Tem certeza que deseja excluir o grupo?',
      );
      if (confirmed) {
        await removePageGroup({
          variables: {
            id: pageGroup?.id,
          },
        });
        toast({
          title: 'Sucess!!',
          description: 'Grupo deletado com sucesso',
          status: 'success',
          position: 'top-right',
        });
        if (onClose) onClose();
      }
    } catch {
      // error
    }
  };

  return (
    <>
      <VStack as="form" spacing="8" onSubmit={handleSubmit(handleForm)}>
        <SimpleGrid minChildWidth="200px" spacing="8" w="100%" />
        <Input
          isRequired
          placeholder="Id da campanha"
          error={errors?.reference_id}
          {...register('reference_id')}
        />
        <Textarea
          placeholder="Descrição"
          error={errors?.description}
          {...register('description')}
        />
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="schedule" mb="0">
            Agendamento
          </FormLabel>
          <Switch
            id="schedule"
            size="lg"
            colorScheme="brand"
            isChecked={schedule}
            {...register('schedule')}
            onChange={() => setSchedule(state => !state)}
          />
        </FormControl>
        {schedule && (
          <>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_start')}
                label="Início"
                showTimeSelect
                error={errors?.schedule_start}
              />
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_end')}
                label="Final"
                showTimeSelect
                error={errors?.schedule_end}
              />
            </SimpleGrid>
            <Checkbox
              alignSelf="flex-start"
              colorScheme="brand"
              {...register('countdown')}
            >
              Contagem Regressiva
            </Checkbox>
          </>
        )}
      </VStack>

      <ModalFooter>
        <HStack spacing={4}>
          {!!pageGroup && (
            <Button
              onClick={handleDelete}
              colorScheme="red"
              isLoading={deleteLoading}
            >
              Excluir
            </Button>
          )}
          <Button onClick={onClose}>Fechar</Button>
          <Button
            colorScheme="brand"
            isLoading={createLoading || updateLoading}
            onClick={handleSubmit(handleForm)}
          >
            Salvar
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
};

export default Form;
